import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandedHomeLink } from "@/components/BrandedHomeLink";
import { LegalSiteLinks } from "@/components/LegalSiteLinks";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FaqAnswer, FaqContactLine } from "@/components/faq/FaqContent";
import { getAuthToken } from "@/App";
import {
  FAQ_CATEGORIES,
  FAQ_PATH,
  faqItemsByCategory,
} from "@/utils/faqItems";

function hashId() {
  if (typeof window === "undefined") return "";
  return (window.location.hash || "").replace(/^#/, "");
}

export default function FaqPage() {
  const navigate = useNavigate();
  const signedIn = Boolean(getAuthToken());
  const [open, setOpen] = useState(() => {
    const id = hashId();
    return id ? [id] : [];
  });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "FAQ | TerraKronos";
    const id = hashId();
    if (id) {
      const el = document.getElementById(id);
      el?.scrollIntoView({ block: "start" });
    }
    const onHash = () => {
      const next = hashId();
      if (next) {
        setOpen((cur) => (cur.includes(next) ? cur : [...cur, next]));
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => {
      document.title = previousTitle;
      window.removeEventListener("hashchange", onHash);
    };
  }, []);

  const onCategoryChange = (categoryId, nextIds) => {
    const inCategory = new Set(faqItemsByCategory(categoryId).map((item) => item.id));
    const rest = open.filter((id) => !inCategory.has(id));
    const merged = [...rest, ...nextIds];
    setOpen(merged);
    const hashTarget = nextIds[nextIds.length - 1] || merged[merged.length - 1];
    const url = hashTarget ? `${FAQ_PATH}#${hashTarget}` : FAQ_PATH;
    window.history.replaceState(null, "", url);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <BrandedHomeLink to={signedIn ? "/dashboard" : "/"} tooltip={false} logoClassName="w-8 h-8">
            <span className="font-bold text-lg hidden sm:inline">TerraKronos</span>
          </BrandedHomeLink>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate(signedIn ? "/dashboard" : "/")}>
              {signedIn ? "Dashboard" : "Home"}
            </Button>
            {!signedIn ? (
              <Button onClick={() => navigate("/login?signup=true")}>Start Free</Button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted/40 text-sm mb-4">
          <HelpCircle className="w-4 h-4 text-primary" />
          No login needed
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-10">
          Frequently asked questions
        </h1>

        <div className="space-y-10">
          {FAQ_CATEGORIES.map((category) => {
            const items = faqItemsByCategory(category.id);
            const categoryIds = new Set(items.map((item) => item.id));
            const categoryOpen = open.filter((id) => categoryIds.has(id));
            return (
              <section key={category.id} aria-labelledby={`faq-${category.id}`}>
                <h2
                  id={`faq-${category.id}`}
                  className="text-xl font-semibold text-foreground mb-3"
                >
                  {category.title}
                </h2>
                <Accordion
                  type="multiple"
                  value={categoryOpen}
                  onValueChange={(nextIds) => onCategoryChange(category.id, nextIds)}
                  className="border border-border rounded-xl px-4 bg-card/40"
                >
                  {items.map((item) => (
                    <AccordionItem key={item.id} value={item.id} id={item.id} className="border-border scroll-mt-24">
                      <AccordionTrigger
                        className="text-base font-semibold text-foreground hover:no-underline py-5"
                      >
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-foreground/90 text-sm leading-relaxed">
                        <FaqAnswer id={item.id} />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            );
          })}
        </div>

        <div className="mt-14 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Open one deal. See if the dates stay straight.</h2>
          <p className="text-muted-foreground mb-6">
            <FaqContactLine />
          </p>
          {!signedIn ? (
            <Button
              size="lg"
              onClick={() => navigate("/login?signup=true")}
              className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-bold text-lg px-8 py-6 h-auto"
              data-testid="faq-cta"
            >
              Start My Free Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          ) : (
            <Button size="lg" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          )}
        </div>

        <footer className="mt-12 pt-8 border-t border-border flex flex-wrap gap-4 text-sm text-muted-foreground">
          <Link to="/learn" className="hover:text-foreground transition-colors">
            Tutorials
          </Link>
          <Link to="/tools" className="hover:text-foreground transition-colors">
            Hawaii Calculators
          </Link>
          <LegalSiteLinks linkClassName="hover:text-foreground transition-colors" />
          <Link to="/" className="hover:text-foreground transition-colors">
            Home
          </Link>
        </footer>
      </main>
    </div>
  );
}
